import { useState, useEffect } from "react";
import { 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  User, 
  Users, 
  Heart, 
  FileText, 
  Scale, 
  CreditCard,
  Send,
  Loader2,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps, stepLabels }) => {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {stepLabels.map((label, index) => {
        const stepNum = index + 1;
        const isActive = stepNum === currentStep;
        const isCompleted = stepNum < currentStep;
        
        return (
          <div key={stepNum} className="flex items-center">
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
              transition-all duration-300
              ${isActive ? 'bg-[#c1ff72] text-black' : ''}
              ${isCompleted ? 'bg-[#c1ff72]/30 text-[#c1ff72]' : ''}
              ${!isActive && !isCompleted ? 'bg-[#1A1A1A] text-white/50 border border-white/10' : ''}
            `}>
              {isCompleted ? <Check className="w-5 h-5" /> : stepNum}
            </div>
            {index < totalSteps - 1 && (
              <div className={`w-8 h-0.5 mx-1 ${stepNum < currentStep ? 'bg-[#c1ff72]' : 'bg-white/10'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// Progress bar component
const ProgressBar = ({ currentStep, totalSteps }) => {
  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;
  
  return (
    <div className="w-full h-1 bg-[#1A1A1A] rounded-full mb-6 overflow-hidden">
      <div 
        className="h-full bg-[#c1ff72] transition-all duration-500 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

export default function AkademieRegistrationForm({ onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [earlyBirdInfo, setEarlyBirdInfo] = useState({ remaining: 10, available: true });
  
  // Calculate current price based on contract type and early bird status
  const getCurrentPrice = () => {
    const isEarlyBird = earlyBirdInfo.available;
    switch (formData.contract_type) {
      case "yearly":
        return isEarlyBird ? 59 : 69;
      case "half_yearly":
        return isEarlyBird ? 69 : 79;
      case "monthly":
        return 89;
      default:
        return 69;
    }
  };
  
  const totalSteps = 7;
  const stepLabels = ["Spieler", "Eltern", "Gesundheit", "Vertrag", "Rechtliches", "Zahlung", "Abschluss"];
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Player data
    player: {
      first_name: "",
      last_name: "",
      birth_date: "",
      gender: "",
      height_cm: "",
      weight_kg: "",
      jersey_size: "",
      position: "",
      strong_foot: "",
      current_club: "",
      team: "",
      age_group: ""
    },
    // Step 2: Parent data
    parent: {
      first_name: "",
      last_name: "",
      street: "",
      house_number: "",
      postal_code: "",
      city: "",
      phone: "",
      email: "",
      phone_secondary: ""
    },
    // Step 3: Health data
    health: {
      allergies: "",
      medications: "",
      conditions: "",
      asthma: false,
      emergency_contact: "",
      notes: ""
    },
    // Step 4: Contract type
    contract_type: "",
    // Step 5: Legal consents
    legal: {
      terms_accepted: false,
      privacy_accepted: false,
      club_priority_accepted: false,
      voluntary_participation: false,
      selection_criteria_accepted: false,
      media_consent: {
        instagram: false,
        tiktok: false,
        youtube: false,
        whatsapp: false,
        website: false
      }
    },
    // Step 6: Payment
    payment: {
      method: "",
      sepa_data: {
        iban: "",
        account_holder: "",
        bic: ""
      }
    }
  });

  // Fetch early bird info
  useEffect(() => {
    const fetchEarlyBird = async () => {
      try {
        const response = await fetch(`${API_URL}/api/akademie/registration-count`);
        if (response.ok) {
          const data = await response.json();
          setEarlyBirdInfo({
            remaining: data.early_bird_remaining,
            available: data.early_bird_available
          });
        }
      } catch (err) {
        console.error("Failed to fetch early bird info:", err);
      }
    };
    fetchEarlyBird();
  }, []);

  // Auto-calculate age group based on birth date
  useEffect(() => {
    if (formData.player.birth_date) {
      const birthYear = parseInt(formData.player.birth_date.split("-")[0]);
      let ageGroup = "";
      if (birthYear >= 2018) {
        ageGroup = "U8/U9";
      } else if (birthYear >= 2016) {
        ageGroup = "U10/U11";
      } else if (birthYear >= 2014) {
        ageGroup = "U12/U13";
      }
      setFormData(prev => ({
        ...prev,
        player: { ...prev.player, age_group: ageGroup }
      }));
    }
  }, [formData.player.birth_date]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateNestedField = (section, subsection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        const p = formData.player;
        return p.first_name?.trim() && 
               p.last_name?.trim() && 
               p.birth_date &&
               p.gender &&
               p.height_cm &&
               p.jersey_size &&
               p.position &&
               p.strong_foot &&
               p.current_club?.trim() &&
               p.team?.trim();
      case 2:
        return formData.parent.first_name &&
               formData.parent.last_name &&
               formData.parent.street &&
               formData.parent.house_number &&
               formData.parent.postal_code &&
               formData.parent.city &&
               formData.parent.phone &&
               formData.parent.email;
      case 3:
        return formData.health.emergency_contact;
      case 4:
        return formData.contract_type;
      case 5:
        return formData.legal.terms_accepted &&
               formData.legal.privacy_accepted &&
               formData.legal.club_priority_accepted &&
               formData.legal.voluntary_participation &&
               formData.legal.selection_criteria_accepted;
      case 6:
        if (!formData.payment.method) return false;
        if (formData.payment.method === "sepa") {
          return formData.payment.sepa_data.iban && formData.payment.sepa_data.account_holder;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      setError(null);
    } else {
      // Show specific missing fields for better UX
      let missingFields = [];
      if (currentStep === 1) {
        const p = formData.player;
        if (!p.first_name?.trim()) missingFields.push("Vorname");
        if (!p.last_name?.trim()) missingFields.push("Nachname");
        if (!p.birth_date) missingFields.push("Geburtsdatum");
        if (!p.gender) missingFields.push("Geschlecht");
        if (!p.height_cm) missingFields.push("Größe");
        if (!p.jersey_size) missingFields.push("Trikotgröße");
        if (!p.position) missingFields.push("Position");
        if (!p.strong_foot) missingFields.push("Starker Fuß");
        if (!p.current_club?.trim()) missingFields.push("Verein");
        if (!p.team?.trim()) missingFields.push("Mannschaft");
      } else if (currentStep === 2) {
        const pa = formData.parent;
        if (!pa.first_name) missingFields.push("Vorname");
        if (!pa.last_name) missingFields.push("Nachname");
        if (!pa.street) missingFields.push("Straße");
        if (!pa.house_number) missingFields.push("Hausnummer");
        if (!pa.postal_code) missingFields.push("PLZ");
        if (!pa.city) missingFields.push("Ort");
        if (!pa.phone) missingFields.push("Telefon");
        if (!pa.email) missingFields.push("E-Mail");
      } else if (currentStep === 3) {
        if (!formData.health.emergency_contact) missingFields.push("Notfallkontakt");
      }
      
      if (missingFields.length > 0) {
        setError(`Bitte fülle folgende Felder aus: ${missingFields.join(", ")}`);
      } else {
        setError("Bitte fülle alle Pflichtfelder aus.");
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/akademie/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          player: {
            ...formData.player,
            height_cm: parseInt(formData.player.height_cm),
            weight_kg: formData.player.weight_kg ? parseInt(formData.player.weight_kg) : null
          },
          parent: formData.parent,
          health: formData.health,
          contract_type: formData.contract_type,
          legal: formData.legal,
          payment: formData.payment
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        onSuccess?.(data);
      } else {
        setError(data.detail || "Anmeldung fehlgeschlagen. Bitte versuche es erneut.");
      }
    } catch (err) {
      setError("Verbindungsfehler. Bitte überprüfe deine Internetverbindung.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getContractPrice = (type) => {
    const prices = {
      yearly: { regular: 69, early: 59 },
      half_yearly: { regular: 79, early: 69 },
      monthly: { regular: 89, early: 89 }
    };
    return earlyBirdInfo.available && type !== "monthly" 
      ? prices[type].early 
      : prices[type].regular;
  };

  // Render step content
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center gap-2">
              <User className="w-6 h-6 text-[#c1ff72]" />
              Spielerdaten
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Vorname *</Label>
                <Input
                  value={formData.player.first_name}
                  onChange={(e) => updateField("player", "first_name", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Nachname *</Label>
                <Input
                  value={formData.player.last_name}
                  onChange={(e) => updateField("player", "last_name", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Geburtsdatum *</Label>
                <Input
                  type="date"
                  value={formData.player.birth_date}
                  onChange={(e) => updateField("player", "birth_date", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Geschlecht *</Label>
                <Select
                  value={formData.player.gender}
                  onValueChange={(value) => updateField("player", "gender", value)}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white mt-1">
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="male">Männlich</SelectItem>
                    <SelectItem value="female">Weiblich</SelectItem>
                    <SelectItem value="diverse">Divers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {formData.player.age_group && (
              <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 p-4 rounded-lg">
                <p className="text-[#c1ff72] font-medium">
                  Automatisch ermittelte Altersklasse: <strong>{formData.player.age_group}</strong>
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-white/70">Größe (cm) *</Label>
                <Input
                  type="number"
                  value={formData.player.height_cm}
                  onChange={(e) => updateField("player", "height_cm", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Gewicht (kg)</Label>
                <Input
                  type="number"
                  value={formData.player.weight_kg}
                  onChange={(e) => updateField("player", "weight_kg", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Trikotgröße *</Label>
                <Select
                  value={formData.player.jersey_size}
                  onValueChange={(value) => updateField("player", "jersey_size", value)}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white mt-1">
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="XS">XS (122-128)</SelectItem>
                    <SelectItem value="S">S (134-140)</SelectItem>
                    <SelectItem value="M">M (146-152)</SelectItem>
                    <SelectItem value="L">L (158-164)</SelectItem>
                    <SelectItem value="XL">XL (170-176)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Position *</Label>
                <Select
                  value={formData.player.position}
                  onValueChange={(value) => updateField("player", "position", value)}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white mt-1">
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="Torwart">Torwart</SelectItem>
                    <SelectItem value="Abwehr">Abwehr</SelectItem>
                    <SelectItem value="Mittelfeld">Mittelfeld</SelectItem>
                    <SelectItem value="Sturm">Sturm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70">Starker Fuß *</Label>
                <Select
                  value={formData.player.strong_foot}
                  onValueChange={(value) => updateField("player", "strong_foot", value)}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white mt-1">
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="right">Rechts</SelectItem>
                    <SelectItem value="left">Links</SelectItem>
                    <SelectItem value="both">Beidfüßig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Aktueller Verein *</Label>
                <Input
                  value={formData.player.current_club}
                  onChange={(e) => updateField("player", "current_club", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Mannschaft *</Label>
                <Input
                  value={formData.player.team}
                  onChange={(e) => updateField("player", "team", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-[#c1ff72]" />
              Erziehungsberechtigter
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Vorname *</Label>
                <Input
                  value={formData.parent.first_name}
                  onChange={(e) => updateField("parent", "first_name", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Nachname *</Label>
                <Input
                  value={formData.parent.last_name}
                  onChange={(e) => updateField("parent", "last_name", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label className="text-white/70">Straße *</Label>
                <Input
                  value={formData.parent.street}
                  onChange={(e) => updateField("parent", "street", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Hausnr. *</Label>
                <Input
                  value={formData.parent.house_number}
                  onChange={(e) => updateField("parent", "house_number", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">PLZ *</Label>
                <Input
                  value={formData.parent.postal_code}
                  onChange={(e) => updateField("parent", "postal_code", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">Ort *</Label>
                <Input
                  value={formData.parent.city}
                  onChange={(e) => updateField("parent", "city", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70">Telefon *</Label>
                <Input
                  type="tel"
                  value={formData.parent.phone}
                  onChange={(e) => updateField("parent", "phone", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">E-Mail *</Label>
                <Input
                  type="email"
                  value={formData.parent.email}
                  onChange={(e) => updateField("parent", "email", e.target.value)}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white/70">Zweite Telefonnummer (optional)</Label>
              <Input
                type="tel"
                value={formData.parent.phone_secondary}
                onChange={(e) => updateField("parent", "phone_secondary", e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
              />
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center gap-2">
              <Heart className="w-6 h-6 text-[#c1ff72]" />
              Gesundheitsdaten
            </h3>
            
            <p className="text-white/60 text-sm">
              Diese Informationen helfen uns, im Notfall richtig zu handeln und auf besondere Bedürfnisse einzugehen.
            </p>
            
            <div>
              <Label className="text-white/70">Allergien</Label>
              <Textarea
                value={formData.health.allergies}
                onChange={(e) => updateField("health", "allergies", e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                placeholder="Falls vorhanden, bitte eintragen"
              />
            </div>
            
            <div>
              <Label className="text-white/70">Medikamente</Label>
              <Textarea
                value={formData.health.medications}
                onChange={(e) => updateField("health", "medications", e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                placeholder="Falls vorhanden, bitte eintragen"
              />
            </div>
            
            <div>
              <Label className="text-white/70">Krankheiten / Einschränkungen</Label>
              <Textarea
                value={formData.health.conditions}
                onChange={(e) => updateField("health", "conditions", e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                placeholder="Falls vorhanden, bitte eintragen"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <Checkbox
                id="asthma"
                checked={formData.health.asthma}
                onCheckedChange={(checked) => updateField("health", "asthma", checked)}
                className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72]"
              />
              <Label htmlFor="asthma" className="text-white cursor-pointer">Asthma vorhanden</Label>
            </div>
            
            <div>
              <Label className="text-white/70">Notfallkontakt (Name + Telefon) *</Label>
              <Input
                value={formData.health.emergency_contact}
                onChange={(e) => updateField("health", "emergency_contact", e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white/70">Sonstige Hinweise</Label>
              <Textarea
                value={formData.health.notes}
                onChange={(e) => updateField("health", "notes", e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                placeholder="Falls vorhanden, bitte eintragen"
              />
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#c1ff72]" />
              Vertragsmodell wählen
            </h3>
            
            {earlyBirdInfo.available && (
              <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 p-4 rounded-lg">
                <p className="text-[#c1ff72] font-medium">
                  Frühbucher-Rabatt: Noch {earlyBirdInfo.remaining} von 10 Plätzen verfügbar!
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Yearly */}
              <div 
                onClick={() => setFormData(prev => ({ ...prev, contract_type: "yearly" }))}
                className={`p-6 rounded-lg cursor-pointer transition-all ${
                  formData.contract_type === "yearly" 
                    ? "bg-[#c1ff72]/20 border-2 border-[#c1ff72]" 
                    : "bg-[#1A1A1A] border-2 border-white/10 hover:border-white/30"
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <h4 className="font-['Bebas_Neue'] text-xl text-white">Jahresvertrag</h4>
                  {earlyBirdInfo.available && (
                    <span className="text-xs bg-[#c1ff72] text-black px-2 py-1 rounded">-10€</span>
                  )}
                </div>
                <p className="text-3xl font-bold text-white mb-1">
                  {getContractPrice("yearly")}€
                  <span className="text-sm font-normal text-white/60">/Monat</span>
                </p>
                {earlyBirdInfo.available && (
                  <p className="text-white/50 text-sm line-through">69€/Monat</p>
                )}
                <p className="text-white/60 text-sm mt-2">12 Monate Laufzeit</p>
              </div>
              
              {/* Half-yearly */}
              <div 
                onClick={() => setFormData(prev => ({ ...prev, contract_type: "half_yearly" }))}
                className={`p-6 rounded-lg cursor-pointer transition-all relative ${
                  formData.contract_type === "half_yearly" 
                    ? "bg-[#c1ff72]/20 border-2 border-[#c1ff72]" 
                    : "bg-[#1A1A1A] border-2 border-white/10 hover:border-white/30"
                }`}
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c1ff72] text-black text-xs font-bold px-3 py-1 rounded">
                  EMPFOHLEN
                </div>
                <div className="flex justify-between items-start mb-4">
                  <h4 className="font-['Bebas_Neue'] text-xl text-white">Halbjahresvertrag</h4>
                  {earlyBirdInfo.available && (
                    <span className="text-xs bg-[#c1ff72] text-black px-2 py-1 rounded">-10€</span>
                  )}
                </div>
                <p className="text-3xl font-bold text-white mb-1">
                  {getContractPrice("half_yearly")}€
                  <span className="text-sm font-normal text-white/60">/Monat</span>
                </p>
                {earlyBirdInfo.available && (
                  <p className="text-white/50 text-sm line-through">79€/Monat</p>
                )}
                <p className="text-white/60 text-sm mt-2">6 Monate Laufzeit</p>
              </div>
              
              {/* Monthly */}
              <div 
                onClick={() => setFormData(prev => ({ ...prev, contract_type: "monthly" }))}
                className={`p-6 rounded-lg cursor-pointer transition-all ${
                  formData.contract_type === "monthly" 
                    ? "bg-[#c1ff72]/20 border-2 border-[#c1ff72]" 
                    : "bg-[#1A1A1A] border-2 border-white/10 hover:border-white/30"
                }`}
              >
                <h4 className="font-['Bebas_Neue'] text-xl text-white mb-4">Monatsvertrag</h4>
                <p className="text-3xl font-bold text-white mb-1">
                  89€
                  <span className="text-sm font-normal text-white/60">/Monat</span>
                </p>
                <p className="text-white/60 text-sm mt-2">Monatlich kündbar</p>
              </div>
            </div>
            
            {/* Included services */}
            <div className="bg-[#1A1A1A] p-6 rounded-lg">
              <h4 className="font-['Bebas_Neue'] text-lg text-white mb-4">Inklusive Leistungen:</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  "4 Trainingseinheiten pro Monat",
                  "Sonntagstraining",
                  "Jeweils 60 Minuten",
                  "Torwarttraining 1x monatlich",
                  "Trainingsshirt",
                  "Trinkflasche",
                  "Sportbeutel",
                  "15% Rabatt auf Camps"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/80 text-sm">
                    <Check className="w-4 h-4 text-[#c1ff72] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-6">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center gap-2">
              <Scale className="w-6 h-6 text-[#c1ff72]" />
              Rechtliche Zustimmungen
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-[#1A1A1A] rounded-lg">
                <Checkbox
                  id="terms"
                  checked={formData.legal.terms_accepted}
                  onCheckedChange={(checked) => updateField("legal", "terms_accepted", checked)}
                  className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72] mt-1"
                />
                <Label htmlFor="terms" className="text-white cursor-pointer">
                  Ich akzeptiere die <span className="text-[#c1ff72]">Vertragsbedingungen</span>. *
                </Label>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-[#1A1A1A] rounded-lg">
                <Checkbox
                  id="privacy"
                  checked={formData.legal.privacy_accepted}
                  onCheckedChange={(checked) => updateField("legal", "privacy_accepted", checked)}
                  className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72] mt-1"
                />
                <Label htmlFor="privacy" className="text-white cursor-pointer">
                  Ich akzeptiere die <span className="text-[#c1ff72]">Datenschutzbestimmungen</span>. *
                </Label>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-[#1A1A1A] rounded-lg">
                <Checkbox
                  id="club_priority"
                  checked={formData.legal.club_priority_accepted}
                  onCheckedChange={(checked) => updateField("legal", "club_priority_accepted", checked)}
                  className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72] mt-1"
                />
                <Label htmlFor="club_priority" className="text-white cursor-pointer">
                  Ich akzeptiere den <span className="text-[#c1ff72]">Vereinsvorrang</span>. *
                </Label>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-[#1A1A1A] rounded-lg">
                <Checkbox
                  id="voluntary"
                  checked={formData.legal.voluntary_participation}
                  onCheckedChange={(checked) => updateField("legal", "voluntary_participation", checked)}
                  className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72] mt-1"
                />
                <Label htmlFor="voluntary" className="text-white cursor-pointer">
                  Ich bestätige, dass die Teilnahme <span className="text-[#c1ff72]">freiwillig</span> erfolgt. *
                </Label>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-[#1A1A1A] rounded-lg">
                <Checkbox
                  id="selection"
                  checked={formData.legal.selection_criteria_accepted}
                  onCheckedChange={(checked) => updateField("legal", "selection_criteria_accepted", checked)}
                  className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72] mt-1"
                />
                <Label htmlFor="selection" className="text-white cursor-pointer">
                  Ich akzeptiere die <span className="text-[#c1ff72]">Auswahlkriterien für Turniere</span>. *
                </Label>
              </div>
            </div>
            
            {/* Media consent */}
            <div className="bg-[#1A1A1A] p-6 rounded-lg">
              <h4 className="font-medium text-white mb-4">
                Foto- und Videoeinwilligung (optional)
              </h4>
              <p className="text-white/60 text-sm mb-4">
                Ich erlaube die Veröffentlichung von Fotos und Videos meines Kindes auf folgenden Plattformen:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {["instagram", "tiktok", "youtube", "whatsapp", "website"].map((platform) => (
                  <div key={platform} className="flex items-center gap-2">
                    <Checkbox
                      id={platform}
                      checked={formData.legal.media_consent[platform]}
                      onCheckedChange={(checked) => updateNestedField("legal", "media_consent", platform, checked)}
                      className="border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:border-[#c1ff72]"
                    />
                    <Label htmlFor={platform} className="text-white capitalize cursor-pointer">
                      {platform}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        
      case 6:
        const currentPrice = getCurrentPrice();
        const playerName = `${formData.player.first_name} ${formData.player.last_name}`.trim() || "Spieler";
        
        return (
          <div className="space-y-6">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-[#c1ff72]" />
              Zahlungsart
            </h3>
            
            {/* Price Display */}
            <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 p-4 rounded-lg">
              <p className="text-white/70 text-sm">Dein monatlicher Beitrag:</p>
              <p className="text-3xl font-bold text-[#c1ff72]">{currentPrice}€ <span className="text-lg font-normal text-white/60">/ Monat</span></p>
              {earlyBirdInfo.available && formData.contract_type !== "monthly" && (
                <p className="text-[#c1ff72] text-sm mt-1">🎉 Frühbucher-Rabatt aktiv!</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* PayPal */}
              <div 
                onClick={() => setFormData(prev => ({ ...prev, payment: { ...prev.payment, method: "paypal" } }))}
                className={`p-6 rounded-lg cursor-pointer transition-all text-center ${
                  formData.payment.method === "paypal" 
                    ? "bg-[#c1ff72]/20 border-2 border-[#c1ff72]" 
                    : "bg-[#1A1A1A] border-2 border-white/10 hover:border-white/30"
                }`}
              >
                <div className="text-3xl mb-2">💳</div>
                <h4 className="font-['Bebas_Neue'] text-xl text-white">PayPal</h4>
                <p className="text-white/60 text-sm mt-2">Sofortige Zahlung</p>
              </div>
              
              {/* SEPA */}
              <div 
                onClick={() => setFormData(prev => ({ ...prev, payment: { ...prev.payment, method: "sepa" } }))}
                className={`p-6 rounded-lg cursor-pointer transition-all text-center ${
                  formData.payment.method === "sepa" 
                    ? "bg-[#c1ff72]/20 border-2 border-[#c1ff72]" 
                    : "bg-[#1A1A1A] border-2 border-white/10 hover:border-white/30"
                }`}
              >
                <div className="text-3xl mb-2">🏦</div>
                <h4 className="font-['Bebas_Neue'] text-xl text-white">SEPA-Lastschrift</h4>
                <p className="text-white/60 text-sm mt-2">Automatische Abbuchung</p>
              </div>
              
              {/* Bank Transfer */}
              <div 
                onClick={() => setFormData(prev => ({ ...prev, payment: { ...prev.payment, method: "bank_transfer" } }))}
                className={`p-6 rounded-lg cursor-pointer transition-all text-center ${
                  formData.payment.method === "bank_transfer" 
                    ? "bg-[#c1ff72]/20 border-2 border-[#c1ff72]" 
                    : "bg-[#1A1A1A] border-2 border-white/10 hover:border-white/30"
                }`}
              >
                <div className="text-3xl mb-2">📄</div>
                <h4 className="font-['Bebas_Neue'] text-xl text-white">Überweisung</h4>
                <p className="text-white/60 text-sm mt-2">Manuelle Zahlung</p>
              </div>
            </div>
            
            {/* PayPal Direct Link */}
            {formData.payment.method === "paypal" && (
              <div className="bg-[#0070ba]/10 border border-[#0070ba]/30 p-6 rounded-lg">
                <h4 className="font-medium text-white mb-3">Direkt mit PayPal bezahlen</h4>
                <p className="text-white/70 text-sm mb-4">
                  Klicke auf den Button um direkt {currentPrice}€ zu bezahlen:
                </p>
                <a 
                  href={`https://paypal.me/aktasyasin/${currentPrice}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#0070ba] hover:bg-[#005ea6] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  💳 Jetzt {currentPrice}€ mit PayPal zahlen
                </a>
                <p className="text-white/50 text-xs mt-3">
                  Der Link öffnet sich in einem neuen Tab. Nach der Zahlung kannst du hier fortfahren.
                </p>
              </div>
            )}
            
            {/* SEPA Form */}
            {formData.payment.method === "sepa" && (
              <div className="bg-[#1A1A1A] p-6 rounded-lg space-y-4">
                <h4 className="font-medium text-white">SEPA-Lastschriftmandat</h4>
                <p className="text-white/60 text-sm">Monatlich werden {currentPrice}€ abgebucht.</p>
                <div>
                  <Label className="text-white/70">IBAN *</Label>
                  <Input
                    value={formData.payment.sepa_data.iban}
                    onChange={(e) => updateNestedField("payment", "sepa_data", "iban", e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Kontoinhaber *</Label>
                  <Input
                    value={formData.payment.sepa_data.account_holder}
                    onChange={(e) => updateNestedField("payment", "sepa_data", "account_holder", e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">BIC (optional)</Label>
                  <Input
                    value={formData.payment.sepa_data.bic}
                    onChange={(e) => updateNestedField("payment", "sepa_data", "bic", e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  />
                </div>
              </div>
            )}
            
            {/* Bank Transfer Info */}
            {formData.payment.method === "bank_transfer" && (
              <div className="bg-[#1A1A1A] p-6 rounded-lg">
                <h4 className="font-medium text-white mb-4">Überweise {currentPrice}€ an:</h4>
                <div className="space-y-3 text-white/80 bg-[#0A0A0A] p-4 rounded-lg font-mono text-sm">
                  <p><span className="text-white/50">Empfänger:</span> <span className="text-white">Yasin Aktas</span></p>
                  <p><span className="text-white/50">IBAN:</span> <span className="text-white select-all">DE21 5002 0406 4225 30</span></p>
                  <p><span className="text-white/50">BIC:</span> <span className="text-white">DEFFDEFFXXX</span></p>
                  <p><span className="text-white/50">Bank:</span> <span className="text-white">C24 Bank</span></p>
                  <p><span className="text-white/50">Betrag:</span> <span className="text-[#c1ff72] font-bold">{currentPrice}€</span></p>
                  <p><span className="text-white/50">Verwendungszweck:</span> <span className="text-white">Akademie {playerName}</span></p>
                </div>
                <p className="text-white/50 text-xs mt-3">
                  Tipp: Du kannst die IBAN markieren und kopieren.
                </p>
              </div>
            )}
          </div>
        );
        
      case 7:
        const contractNames = {
          yearly: "Jahresvertrag",
          half_yearly: "Halbjahresvertrag",
          monthly: "Monatsvertrag"
        };
        
        return (
          <div className="space-y-6">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white flex items-center gap-2">
              <Send className="w-6 h-6 text-[#c1ff72]" />
              Anmeldung abschließen
            </h3>
            
            <div className="bg-[#1A1A1A] p-6 rounded-lg">
              <h4 className="font-medium text-white mb-4">Zusammenfassung</h4>
              
              <div className="space-y-4 text-sm">
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/60">Spieler</span>
                  <span className="text-white">{formData.player.first_name} {formData.player.last_name}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/60">Altersklasse</span>
                  <span className="text-white">{formData.player.age_group}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/60">Vertragsart</span>
                  <span className="text-white">{contractNames[formData.contract_type]}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-2">
                  <span className="text-white/60">Monatsbeitrag</span>
                  <span className="text-[#c1ff72] font-bold">{getContractPrice(formData.contract_type)}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Zahlungsart</span>
                  <span className="text-white">
                    {formData.payment.method === "paypal" && "PayPal"}
                    {formData.payment.method === "sepa" && "SEPA-Lastschrift"}
                    {formData.payment.method === "bank_transfer" && "Überweisung"}
                  </span>
                </div>
              </div>
            </div>
            
            {earlyBirdInfo.available && formData.contract_type !== "monthly" && (
              <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 p-4 rounded-lg text-center">
                <p className="text-[#c1ff72] font-bold">
                  🎉 Du erhältst den Frühbucher-Rabatt von 10€/Monat!
                </p>
              </div>
            )}
            
            <p className="text-white/60 text-sm">
              Nach der Anmeldung erhältst du eine E-Mail mit dem Vertrag als PDF. 
              {formData.payment.method === "paypal" && " Du wirst anschließend zu PayPal weitergeleitet."}
            </p>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 p-6 z-10">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="font-['Bebas_Neue'] text-3xl text-white">Online-Anmeldung</h2>
              <p className="text-white/60">Schritt {currentStep} von {totalSteps}: {stepLabels[currentStep - 1]}</p>
            </div>
            <button 
              onClick={onClose}
              className="text-white/50 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>
          
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} stepLabels={stepLabels} />
        </div>
        
        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-400">{error}</p>
            </div>
          )}
          
          {renderStep()}
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-white/10 p-6 flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? onClose : prevStep}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {currentStep === 1 ? "Abbrechen" : "Zurück"}
          </Button>
          
          {currentStep < totalSteps ? (
            <Button
              onClick={nextStep}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-black font-bold"
            >
              Weiter
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#c1ff72] hover:bg-[#d4ff9a] text-black font-bold min-w-[200px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird verarbeitet...
                </>
              ) : (
                <>
                  Jetzt verbindlich anmelden
                  <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
